import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LANGUAGES } from '../i18n/config';

/**
 * SEO component for managing meta tags
 * Requirements: 11.1, 11.3
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} [props.image] - Open Graph image URL
 * @param {string} [props.url] - Canonical URL
 * @param {string} [props.type='website'] - Open Graph type (website, article, etc.)
 * @param {string} [props.author] - Article author (for article pages)
 * @param {string} [props.publishedTime] - Article published time (for article pages)
 * @param {string[]} [props.keywords] - SEO keywords
 * @param {Object} [props.structuredData] - Schema.org structured data
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  keywords = [],
  structuredData,
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';
  const availableLanguages = LANGUAGES;
  const location = useLocation();
  
  // Default values
  const siteTitle = 'VASTP - Vietnamese Association for Science and Technology in Poland';
  const defaultDescription = 'Vietnamese Association for Science and Technology in Poland - News, conferences, and research collaboration';
  const defaultImage = `${window.location.origin}/logo512.png`;
  
  // Construct full title
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  const metaUrl = url || window.location.href;
  
  // Generate hreflang URLs for all languages
  const baseUrl = window.location.origin;
  const pathWithoutLang = location.pathname.replace(/^\/(en|vi|pl)/, '');
  const hreflangs = Object.keys(availableLanguages).map(lang => ({
    lang,
    url: `${baseUrl}/${lang}${pathWithoutLang}${location.search}`
  }));

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={metaUrl} />
      
      {/* Hreflang tags for multilingual SEO */}
      {hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      {/* x-default hreflang pointing to English */}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en${pathWithoutLang}${location.search}`} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={currentLanguage} />
      {/* Alternate locales for Open Graph */}
      {Object.keys(availableLanguages)
        .filter(lang => lang !== currentLanguage)
        .map(lang => (
          <meta key={`og-locale-${lang}`} property="og:locale:alternate" content={lang} />
        ))
      }

      {/* Article-specific Open Graph tags */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content={currentLanguage} />
      <meta name="revisit-after" content="7 days" />

      {/* Structured Data (Schema.org) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.oneOf(['website', 'article']),
  author: PropTypes.string,
  publishedTime: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  structuredData: PropTypes.object,
};

export default SEO;
